import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { fmtINR, fmtDateDMY, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { AlertTriangle, XCircle, Clock, CheckCircle2, FileWarning } from "lucide-react";

export default function ManagerQueue() {
  const [openFlags, setOpenFlags] = useState([]);
  const [nearEscalation, setNearEscalation] = useState([]);
  const [openRecons, setOpenRecons] = useState([]);
  const [missingProofs, setMissingProofs] = useState(0);
  const [unreadable, setUnreadable] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [flagsR, digestR] = await Promise.all([
        api.get("/flags/?status=OPEN"),
        api.get(`/reports/daily_digest/${todayISO()}`),
      ]);
      const flags = flagsR.data || [];
      setOpenFlags(flags);
      // near-escalation: OPEN or ACK, older than 24h and impact >= 5000 (approaching 48h threshold)
      const nowMs = Date.now();
      const near = flags.filter((f) => {
        const created = new Date(f.created_at).getTime();
        const hoursOld = (nowMs - created) / 36e5;
        return hoursOld >= 24 && hoursOld < 48 && (f.rupee_impact || 0) >= 5000 && !f.escalated;
      });
      setNearEscalation(near);
      setMissingProofs(digestR.data?.missing_proofs || 0);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const ack = async (id) => { try { await api.post(`/flags/${id}/ack`); load(); } catch (e) { toast.error(apiError(e)); } };
  const resolve = async (id) => {
    const note = prompt("Resolution note?");
    if (!note) return;
    try { await api.post(`/flags/${id}/resolve`, { resolution_note: note }); toast.success("Resolved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const totalImpact = openFlags.reduce((a, f) => a + (f.rupee_impact || 0), 0);
  const highSev = openFlags.filter((f) => f.severity === "HIGH");

  return (
    <div className="space-y-4" data-testid="manager-queue-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Review Queue</h1>
        <p className="text-sm text-slate-600 mt-1">Everything that needs your decision. Sort: rupee impact desc.</p>
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Open flags" value={openFlags.length} icon={AlertTriangle} tone={openFlags.length > 0 ? "danger" : "ok"} testId="mq-open-count" />
        <StatCard label="High severity" value={highSev.length} icon={AlertTriangle} tone={highSev.length > 0 ? "danger" : "ok"} testId="mq-high-count" />
        <StatCard label="Total exposure ₹" value={fmtINR(totalImpact)} icon={FileWarning} tone={totalImpact > 5000 ? "danger" : "warn"} big testId="mq-total" />
        <StatCard label="Missing proofs" value={missingProofs} icon={XCircle} tone={missingProofs > 0 ? "warn" : "ok"} testId="mq-missing" />
      </div>

      {/* Near-escalation warning */}
      {nearEscalation.length > 0 && (
        <Card data-testid="near-escalation-card" className="p-4 border-2 border-amber-500 bg-amber-50 rounded-sm">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={18} className="text-amber-700" />
            <h3 className="font-bold text-amber-900 uppercase tracking-wider text-sm">Escalating soon — resolve within {Math.max(0, 48 - Math.floor((Date.now() - new Date(nearEscalation[0].created_at).getTime()) / 36e5))} hours</h3>
          </div>
          <p className="text-xs text-amber-800 mb-2">
            These are &gt; 24h old with ₹ impact ≥ ₹5,000. If unresolved at 48h, they auto-escalate to the owner's inbox.
          </p>
          <ul className="space-y-1">
            {nearEscalation.map((f) => (
              <li key={f.flag_id} className="flex items-center justify-between text-sm bg-white border border-amber-300 p-2 rounded-sm">
                <span className="truncate">{f.type} · {f.actor_ref} · {f.message}</span>
                <span className="num font-bold text-amber-700 ml-2 whitespace-nowrap">{fmtINR(f.rupee_impact)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Main queue */}
      <Card className="p-3 border-2 border-slate-300 rounded-sm">
        <h3 className="font-bold text-slate-800 mb-2">Open Flags — sorted by ₹ impact</h3>
        {openFlags.length === 0 && (
          <div className="p-6 text-center">
            <CheckCircle2 size={36} className="text-emerald-600 mx-auto mb-2" />
            <div className="font-bold text-slate-900">All clear.</div>
            <div className="text-sm text-slate-600 mt-1">No open flags. Good work.</div>
          </div>
        )}
        <div className="space-y-2">
          {openFlags.map((f) => (
            <div key={f.flag_id} data-testid={`queue-flag-${f.flag_id}`} className="flex items-start justify-between gap-2 border-2 border-slate-200 p-3 rounded-sm">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <AlertTriangle size={14} className={f.severity === "HIGH" ? "text-red-600" : f.severity === "MED" ? "text-amber-600" : "text-slate-500"} />
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{f.type}</span>
                  <span className={"text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border " + (f.severity === "HIGH" ? "state-danger" : f.severity === "MED" ? "state-warn" : "state-info")}>{f.severity}</span>
                </div>
                <div className="text-sm text-slate-900 mt-1">{f.message}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <b>{f.actor_ref}</b> · {fmtDateDMY(f.date)} {f.shift_code} · created {new Date(f.created_at).toLocaleString()}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="num text-lg font-bold text-red-600">{fmtINR(f.rupee_impact)}</div>
                <div className="flex gap-1 mt-2">
                  <Button size="sm" variant="outline" onClick={() => ack(f.flag_id)} className="text-xs h-7 border-slate-300" data-testid={`queue-ack-${f.flag_id}`}>Ack</Button>
                  <Button size="sm" onClick={() => resolve(f.flag_id)} className="text-xs h-7 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`queue-resolve-${f.flag_id}`}>Resolve</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex gap-2">
        <Link to="/flags"><Button variant="outline" className="border-slate-300">See all flags (incl. resolved) →</Button></Link>
        <Link to="/reconciliation"><Button variant="outline" className="border-slate-300">Reconciliation →</Button></Link>
        <Link to="/fraud"><Button variant="outline" className="border-slate-300">Red Flags →</Button></Link>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone, big, testId }) {
  const bg = tone === "danger" ? "bg-red-50 border-red-500" : tone === "warn" ? "bg-amber-50 border-amber-500" : "bg-emerald-50 border-emerald-500";
  const numColor = tone === "danger" ? "text-red-700" : tone === "warn" ? "text-amber-800" : "text-emerald-800";
  return (
    <div data-testid={testId} className={`p-3 border-2 rounded-sm ${bg}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{label}</div>
        {Icon && <Icon size={14} className="text-slate-500" />}
      </div>
      <div className={`font-mono font-bold mt-1 ${numColor} ${big ? "text-2xl" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
